





















































import { computed, defineComponent, PropType } from "@vue/composition-api";
import {
  FundraisingGoalReward,
  FundraisingGoalRewardStatus,
  FundraisingGoalStatus,
  InputFundraisingGoalRewardCreate,
} from "@scrile/api-provider/dist/api/FundraisingGoalsProvider";
import useBlobUrl from "@/hooks/useBlobUrl";
import { $t, $tc } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getProfileUrl } from "@/lib/urlHelpers";
import { QuickTip } from "@/types";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseChip from "@/components/BaseChip";
import BaseIconButton from "@/components/BaseIconButton";
import BaseImage from "@/components/BaseImage/BaseImage";
import FormatMoney from "@/components/FormatMoney";
import QuickTipsItem from "@/components/QuickTipsItem";

const tipItemsMap: Record<string, QuickTip> = {
  [FundraisingGoalRewardStatus.NEW]: {
    icon: "sci-progress-check",
    type: "info",
    translateKey: $t("This reward is being verified"),
  },
  [FundraisingGoalRewardStatus.DECLINED]: {
    icon: "msr-warning",
    type: "warning",
    translateKey: $t("Decline reason was not provided"),
  },
  [FundraisingGoalRewardStatus.DELETED]: {
    icon: "msr-delete",
    type: "warning",
    translateKey: $t("Reward deleted"),
  },
};

export default defineComponent({
  name: "FundraisingGoalRewardCard",
  components: {
    BaseButton,
    QuickTipsItem,
    BaseIconButton,
    BaseIcon,
    FormatMoney,
    BaseChip,
    BaseImage,
  },
  props: {
    reward: {
      type: Object as PropType<FundraisingGoalReward | Omit<InputFundraisingGoalRewardCreate, "goalId">>,
      required: true,
    },
    file: { type: File },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    canPurchase: { type: Boolean, default: false },
    showGoalButton: { type: Boolean, default: false },
    hideLeftCount: { type: Boolean, default: false },
  },
  setup(props) {
    const { getBlobUrl } = useBlobUrl();

    const coverSrc = computed(() => {
      if ("file" in props.reward && props.reward.file)
        return MediaFilesController.getImageThumbnail(props.reward.file.urlPart, "720p");
      if (props.file) return getBlobUrl(props.file);
      else return "";
    });
    const countInfo = computed(() => {
      if (!props.reward.totalCount) return $t("Unlimited rewards");
      if ("id" in props.reward) {
        return $t("{0} left", [$tc($t("reward"), props.reward.totalCount - props.reward.purchasedCount)]);
      }
      return $tc($t("reward"), props.reward.totalCount);
    });

    const isDeclined = computed(
      () => "status" in props.reward && props.reward.status === FundraisingGoalRewardStatus.DECLINED
    );
    const isUnderVerification = computed(
      () => "status" in props.reward && props.reward.status === FundraisingGoalRewardStatus.NEW
    );
    const isDeleted = computed(
      () => "status" in props.reward && props.reward.status === FundraisingGoalRewardStatus.DELETED
    );
    const isActive = computed(() => !(isDeclined.value || isUnderVerification.value || isDeleted.value));

    const tipItem = computed<QuickTip | null>(() => {
      if (isActive.value || !("status" in props.reward)) return null;
      let resp = tipItemsMap[props.reward.status];
      if (isDeclined.value && "declineReason" in props.reward && props.reward.declineReason) {
        resp = { ...resp, translateKey: props.reward.declineReason };
      }
      return resp;
    });

    const purchasedTipItem = computed(() => {
      if (!props.canPurchase || !("status" in props.reward) || !props.reward.purchasedByMe) return null;
      return {
        translateKey: $t("You donated for this reward"),
        icon: "sci-check-line",
        type: "info",
      };
    });

    const purchaseEnabled = computed(() => {
      if ("purchasedCount" in props.reward) {
        return !props.reward.totalCount || props.reward.purchasedCount < props.reward.totalCount;
      }
      return false;
    });

    const purchaseButtonText = computed(() => {
      if ("purchasedByMe" in props.reward && props.reward.purchasedByMe) {
        return $t("Get another reward");
      }
      return $t("Get reward");
    });

    const goalButtonLocation = computed(() => {
      if (
        !props.showGoalButton ||
        !("goal" in props.reward) ||
        !props.reward.goal ||
        props.reward.goal.status !== FundraisingGoalStatus.ACTIVE
      ) {
        return null;
      }

      return getProfileUrl(props.reward.goal.user);
    });

    return {
      coverSrc,
      countInfo,
      tipItem,
      isDeleted,
      isDeclined,
      tipItemsMap,
      purchaseEnabled,
      purchasedTipItem,
      purchaseButtonText,
      goalButtonLocation,
    };
  },
});
